import React from "react";
import { Trash2 } from "lucide-react";
import { shipmentUnitTypeOptions } from "src/helpers/shipmentHelper";
import { matchCase } from "src/utils";
import { ShipmentUnitsColumnHeaderImperial, ShipmentUnitsColumnHeaderMetric } from "src/enums";
import { ShipmentUnitInputField, UoM, TableColumn } from "src/types";
import { TextInputBox } from "src/components";
import { Table } from "src/components";

interface ShipmentUnitInputTableProps {
  shipmentUnits: ShipmentUnitInputField[];
  setShipmentUnits: React.Dispatch<React.SetStateAction<ShipmentUnitInputField[]>>;
  minShipmentUnits?: number;
  shipmentUnitUoM?: UoM;
}

const ShipmentUnitInputTable: React.FC<ShipmentUnitInputTableProps> = ({
  shipmentUnits,
  setShipmentUnits,
  minShipmentUnits = 0,
  shipmentUnitUoM = "Metric"
}) => {
  const handleProductCheckInsInputChange = <T,>(id: string, fieldName: keyof ShipmentUnitInputField, value: T) => {
    setShipmentUnits(
      shipmentUnits.map((productCheckIn) =>
        productCheckIn.fieldId === id ? { ...productCheckIn, [fieldName]: value } : productCheckIn
      )
    );
  };

  const shipmentUnitHeaderEnum =
    shipmentUnitUoM === "Imperial" ? ShipmentUnitsColumnHeaderImperial : ShipmentUnitsColumnHeaderMetric;

  const ShipmentUnitTableColumns: TableColumn<
    ShipmentUnitsColumnHeaderImperial | ShipmentUnitsColumnHeaderMetric,
    ShipmentUnitInputField
  >[] = [
    {
      header: shipmentUnitHeaderEnum.TYPE,
      column: (row: ShipmentUnitInputField) => (
        <div className="w-24">
          <TextInputBox
            id={`${row.type}-type-${row.fieldId}`}
            value={row.type ?? ""}
            onChange={(value) =>
              handleProductCheckInsInputChange(row.fieldId, "type", matchCase(value, shipmentUnitTypeOptions))
            }
            maxLength={10}
            placeholder="Type"
            size="sm"
            dropDownOptions={["Pallets", "Boxes"]}
          />
        </div>
      ),
      styles: { minWidth: "120px" }
    },
    {
      header: shipmentUnitHeaderEnum.QUANTITY,
      column: (row: ShipmentUnitInputField) => (
        <div className="w-20">
          <TextInputBox
            id={`${row.type}-quantity-${row.fieldId}`}
            value={row.quantity ?? ""}
            onChange={(value) => handleProductCheckInsInputChange(row.fieldId, "quantity", value)}
            maxLength={10}
            placeholder="Qty"
            size="sm"
          />
        </div>
      ),
      styles: { minWidth: "100px" }
    },
    {
      header: shipmentUnitHeaderEnum.WEIGHT,
      column: (row: ShipmentUnitInputField) => (
        <div className="w-20">
          <TextInputBox
            id={`${row.type}-weight-${row.fieldId}`}
            value={row.weight ?? ""}
            onChange={(value) => handleProductCheckInsInputChange(row.fieldId, "weight", value)}
            maxLength={10}
            placeholder="Weight"
            size="sm"
          />
        </div>
      ),
      styles: { minWidth: "100px" }
    },
    {
      header: shipmentUnitHeaderEnum.LENGTH,
      column: (row: ShipmentUnitInputField) => (
        <div className="w-20">
          <TextInputBox
            id={`${row.type}-length-${row.fieldId}`}
            value={row.length ?? ""}
            onChange={(value) => handleProductCheckInsInputChange(row.fieldId, "length", value)}
            maxLength={10}
            placeholder="Length"
            size="sm"
          />
        </div>
      ),
      styles: { minWidth: "100px" }
    },
    {
      header: shipmentUnitHeaderEnum.WIDTH,
      column: (row: ShipmentUnitInputField) => (
        <div className="w-20">
          <TextInputBox
            id={`${row.type}-width-${row.fieldId}`}
            value={row.width ?? ""}
            onChange={(value) => handleProductCheckInsInputChange(row.fieldId, "width", value)}
            maxLength={10}
            placeholder="Width"
            size="sm"
          />
        </div>
      ),
      styles: { minWidth: "100px" }
    },
    {
      header: shipmentUnitHeaderEnum.HEIGHT,
      column: (row: ShipmentUnitInputField) => (
        <div className="w-20">
          <TextInputBox
            id={`${row.type}-height-${row.fieldId}`}
            value={row.height ?? ""}
            onChange={(value) => handleProductCheckInsInputChange(row.fieldId, "height", value)}
            maxLength={10}
            placeholder="Height"
            size="sm"
          />
        </div>
      ),
      styles: { minWidth: "100px" }
    },
    {
      header: shipmentUnitHeaderEnum.PLACE_HOLDER,
      column: (row: ShipmentUnitInputField) => (
        <button
          onClick={() => {
            if (shipmentUnits.length > minShipmentUnits) {
              setShipmentUnits(shipmentUnits.filter((productCheckIn) => productCheckIn.fieldId !== row.fieldId));
            }
          }}
          disabled={shipmentUnits.length <= minShipmentUnits}
          className="group flex items-center justify-center p-2 rounded-md hover:bg-error-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
          title="Delete shipment unit"
          type="button"
        >
          <Trash2 className="w-4 h-4 text-error-600 group-hover:text-error-700 transition-colors" />
        </button>
      ),
      styles: { minWidth: "60px", textAlign: "center" }
    }
  ];

  return <Table columns={ShipmentUnitTableColumns} data={shipmentUnits} />;
};

export { ShipmentUnitInputTable };
